import {
  assignableMembersResponseSchema,
  conversationDtoSchema,
  conversationListResponseSchema,
  markConversationUnreadResponseSchema,
  updateConversationAssignmentRequestSchema,
  updateConversationStatusRequestSchema,
  createMessageRequestSchema,
  createTemplateMessageRequestSchema,
  inboxUnreadSummaryResponseSchema,
  markConversationReadRequestSchema,
  markConversationReadResponseSchema,
  messageDtoSchema,
  messageListResponseSchema,
  messageTemplateHeaderImageDownloadUrlResponseSchema,
  whatsAppCallingStatusResponseSchema,
  type AssignableMembersResponse,
  type ConversationDto,
  type ConversationExportFormatName,
  type ConversationListResponse,
  type ConversationWorkflowStatusName,
  type MarkConversationUnreadResponse,
  type CreateMessageRequest,
  type CreateTemplateMessageRequest,
  type InboxUnreadSummaryResponse,
  type MarkConversationReadRequest,
  type MarkConversationReadResponse,
  type MessageDto,
  type MessageListResponse,
  type MessageTemplateHeaderImageDownloadUrlResponse,
  type WhatsAppCallingStatusResponse,
} from "@multi-chat/shared";
import { apiDownload, apiGet, apiPost, apiPut } from "./client.js";
import {
  conversationListPath,
  type ConversationListClientQuery,
} from "./conversation-list-query.js";

export const CONVERSATION_PAGE_SIZE = 20;
export const MESSAGE_PAGE_SIZE = 50;

export type InboxConversationListParams = ConversationListClientQuery & {
  signal?: AbortSignal;
};

export interface InboxListParams {
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
}

function orgConversationsBase(organizationId: number): string {
  return `/organizations/${organizationId}/conversations`;
}

function withCursorQuery(
  path: string,
  params?: { cursor?: string; limit?: number },
): string {
  const query = new URLSearchParams();
  if (params?.cursor) {
    query.set("cursor", params.cursor);
  }
  if (params?.limit != null) {
    query.set("limit", String(params.limit));
  }
  const encoded = query.toString();
  return encoded.length > 0 ? `${path}?${encoded}` : path;
}

export async function listConversations(
  organizationId: number,
  params: InboxConversationListParams = {},
): Promise<ConversationListResponse> {
  const { signal, ...query } = params;
  return conversationListResponseSchema.parse(
    await apiGet(conversationListPath(organizationId, query), signal),
  );
}

export async function getInboxUnreadSummary(
  organizationId: number,
  signal?: AbortSignal,
): Promise<InboxUnreadSummaryResponse> {
  return inboxUnreadSummaryResponseSchema.parse(
    await apiGet(`/organizations/${organizationId}/inbox/unread-summary`, signal),
  );
}

export async function getConversation(
  organizationId: number,
  conversationId: number,
  signal?: AbortSignal,
): Promise<ConversationDto> {
  return conversationDtoSchema.parse(
    await apiGet(`${orgConversationsBase(organizationId)}/${conversationId}`, signal),
  );
}

export async function listConversationMessages(
  organizationId: number,
  conversationId: number,
  params: InboxListParams = {},
): Promise<MessageListResponse> {
  const { signal, ...page } = params;
  return messageListResponseSchema.parse(
    await apiGet(
      withCursorQuery(
        `${orgConversationsBase(organizationId)}/${conversationId}/messages`,
        page,
      ),
      signal,
    ),
  );
}

export async function createConversationMessage(
  organizationId: number,
  conversationId: number,
  body: CreateMessageRequest,
  signal?: AbortSignal,
): Promise<MessageDto> {
  const payload = createMessageRequestSchema.parse(body);
  return messageDtoSchema.parse(
    await apiPost(
      `${orgConversationsBase(organizationId)}/${conversationId}/messages`,
      payload,
      signal,
    ),
  );
}

export async function createConversationTemplateMessage(
  organizationId: number,
  conversationId: number,
  body: CreateTemplateMessageRequest,
  signal?: AbortSignal,
): Promise<MessageDto> {
  const payload = createTemplateMessageRequestSchema.parse(body);
  return messageDtoSchema.parse(
    await apiPost(
      `${orgConversationsBase(organizationId)}/${conversationId}/messages/template`,
      payload,
      signal,
    ),
  );
}

export async function createMessageTemplateHeaderImageDownloadUrl(
  organizationId: number,
  conversationId: number,
  messageId: string,
  signal?: AbortSignal,
): Promise<MessageTemplateHeaderImageDownloadUrlResponse> {
  return messageTemplateHeaderImageDownloadUrlResponseSchema.parse(
    await apiPost(
      `${orgConversationsBase(organizationId)}/${conversationId}/messages/${messageId}/template-header-image/download-url`,
      undefined,
      signal,
    ),
  );
}

export async function getWhatsAppCallingStatus(
  organizationId: number,
  conversationId: number,
  signal?: AbortSignal,
): Promise<WhatsAppCallingStatusResponse> {
  return whatsAppCallingStatusResponseSchema.parse(
    await apiGet(
      `${orgConversationsBase(organizationId)}/${conversationId}/whatsapp/calling-status`,
      signal,
    ),
  );
}

export async function markConversationRead(
  organizationId: number,
  conversationId: number,
  body: MarkConversationReadRequest,
  signal?: AbortSignal,
): Promise<MarkConversationReadResponse> {
  const payload = markConversationReadRequestSchema.parse(body);
  return markConversationReadResponseSchema.parse(
    await apiPost(
      `${orgConversationsBase(organizationId)}/${conversationId}/read`,
      payload,
      signal,
    ),
  );
}

export async function listAssignableMembers(
  organizationId: number,
  signal?: AbortSignal,
): Promise<AssignableMembersResponse> {
  return assignableMembersResponseSchema.parse(
    await apiGet(`/organizations/${organizationId}/inbox/assignable-members`, signal),
  );
}

export async function updateConversationAssignment(
  organizationId: number,
  conversationId: number,
  assignedMemberId: number | null,
  signal?: AbortSignal,
): Promise<ConversationDto> {
  const payload = updateConversationAssignmentRequestSchema.parse({ assignedMemberId });
  return conversationDtoSchema.parse(
    await apiPut(
      `${orgConversationsBase(organizationId)}/${conversationId}/assignment`,
      payload,
      signal,
    ),
  );
}

export async function updateConversationStatus(
  organizationId: number,
  conversationId: number,
  status: ConversationWorkflowStatusName,
  signal?: AbortSignal,
): Promise<ConversationDto> {
  const payload = updateConversationStatusRequestSchema.parse({ status });
  return conversationDtoSchema.parse(
    await apiPut(
      `${orgConversationsBase(organizationId)}/${conversationId}/status`,
      payload,
      signal,
    ),
  );
}

export async function markConversationUnread(
  organizationId: number,
  conversationId: number,
  signal?: AbortSignal,
): Promise<MarkConversationUnreadResponse> {
  return markConversationUnreadResponseSchema.parse(
    await apiPost(
      `${orgConversationsBase(organizationId)}/${conversationId}/unread`,
      undefined,
      signal,
    ),
  );
}

export async function downloadConversationExport(
  organizationId: number,
  conversationId: number,
  format: ConversationExportFormatName,
  locale: "en" | "he",
): Promise<void> {
  const params = new URLSearchParams({ format, locale });
  const { blob, filename } = await apiDownload(
    `${orgConversationsBase(organizationId)}/${conversationId}/export?${params.toString()}`,
  );
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const anchor = link;
  anchor.href = href;
  anchor.download = filename ?? `conversation-${conversationId}.${format}`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}
